import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Heart, Phone, Mail, ExternalLink, MessageCircle, Clock, Users, Shield,
  Send, CheckCircle, AlertCircle, Sparkles, Coffee, Sun, Moon, Brain,
  Smile, Frown, Meh, Calendar, Music, Dumbbell, Wind, CloudRain, Waves,
  TreePine, Bird, Flame, Play, Pause, RefreshCw, Plus, Trash2, X, Check,
  Droplets, Footprints, Timer, Bed, Zap, ChevronRight, ChevronDown, Award,
  Target, HeartHandshake, Volume2, VolumeX, Battery, Activity, TrendingUp,
  Search, BookHeart, Quote, Feather, Compass, ThumbsUp
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField, JewelChipRow } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LS = (k) => `wb_${k}`;

/* ─────────────────────────  STATIC DATA  ───────────────────────── */

const MOOD_OPTIONS = [
  { value: 5, icon: Sparkles, label: 'Amazing', color: 'emerald', hex: '#10B981' },
  { value: 4, icon: Smile,    label: 'Good',    color: 'cyan',    hex: '#00E5FF' },
  { value: 3, icon: Meh,      label: 'Okay',    color: 'violet',  hex: '#8A2BE2' },
  { value: 2, icon: Frown,    label: 'Low',     color: 'orange',  hex: '#F97316' },
  { value: 1, icon: AlertCircle, label: 'Struggling', color: 'rose', hex: '#F43F5E' },
];

const BREATHING_PATTERNS = [
  { name: 'Box Breathing',  inhale: 4, hold1: 4, exhale: 4, hold2: 4, desc: 'Balanced calm' },
  { name: '4-7-8 Relaxing', inhale: 4, hold1: 7, exhale: 8, hold2: 0, desc: 'Deep relaxation' },
  { name: 'Energising',     inhale: 6, hold1: 0, exhale: 2, hold2: 0, desc: 'Quick boost' },
  { name: 'Stress Relief',  inhale: 4, hold1: 2, exhale: 6, hold2: 2, desc: 'Release tension' },
];

const AMBIENT_SOUNDS = [
  { name: 'Rain',     icon: CloudRain, hex: '#00E5FF', url: 'https://cdn.pixabay.com/download/audio/2022/03/10/audio_1a8b6b0a11.mp3' },
  { name: 'Ocean',    icon: Waves,     hex: '#00E5FF', url: 'https://cdn.pixabay.com/download/audio/2022/10/25/audio_946bc98f13.mp3' },
  { name: 'Forest',   icon: TreePine,  hex: '#10B981', url: 'https://cdn.pixabay.com/download/audio/2022/03/09/audio_c8e7f9c9a1.mp3' },
  { name: 'Birds',    icon: Bird,      hex: '#8A2BE2', url: 'https://cdn.pixabay.com/download/audio/2022/08/23/audio_e0b3cba97e.mp3' },
  { name: 'Wind',     icon: Wind,      hex: '#94A3B8', url: 'https://cdn.pixabay.com/download/audio/2022/03/29/audio_88447c22f2.mp3' },
  { name: 'Fire',     icon: Flame,     hex: '#F97316', url: 'https://cdn.pixabay.com/download/audio/2022/03/10/audio_2eee2baa2a.mp3' },
];

const DEFAULT_HABITS = [
  { id: 'water',    label: 'Drink water',      icon: 'Droplets',  target: 8, unit: 'glasses', hex: '#00E5FF' },
  { id: 'movement', label: 'Move / stretch',   icon: 'Footprints',target: 3, unit: 'breaks',  hex: '#10B981' },
  { id: 'breath',   label: 'Deep breathing',   icon: 'Wind',      target: 2, unit: 'sessions',hex: '#8A2BE2' },
  { id: 'sunlight', label: 'Sunlight / fresh air', icon: 'Sun',   target: 1, unit: 'trip',    hex: '#F59E0B' },
];
const HABIT_ICONS = { Droplets, Footprints, Wind, Sun, Coffee, Dumbbell, Brain, Heart, Moon, Bed };
const HABIT_ICON_KEYS = ['Droplets', 'Footprints', 'Wind', 'Sun', 'Coffee', 'Dumbbell', 'Brain', 'Heart', 'Moon', 'Bed'];
const HABIT_HEXES = ['#00E5FF', '#10B981', '#8A2BE2', '#F59E0B', '#F43F5E', '#F97316'];

const CRISIS = [
  { name: 'Emergency', phone: '999', urgent: true },
  { name: 'Samaritans', phone: '116 123', link: 'https://samaritans.org' },
  { name: 'SHOUT (text)', phone: 'Text SHOUT to 85258' },
  { name: 'NHS 111', phone: '111' },
];

const RESOURCE_GROUPS = [
  { id: 'mental',   label: 'Mental Health', icon: Brain, hex: '#8A2BE2', items: [
    { name: 'Mind UK',        phone: '0300 123 3393',   link: 'https://www.mind.org.uk' },
    { name: 'Anxiety UK',     phone: '03444 775 774',   link: 'https://www.anxietyuk.org.uk' },
    { name: 'CALM',           phone: '0800 58 58 58',   link: 'https://www.thecalmzone.net' },
    { name: 'Rethink',        phone: '0808 801 0525',   link: 'https://www.rethink.org' },
  ]},
  { id: 'financial', label: 'Financial', icon: Target, hex: '#10B981', items: [
    { name: 'Money Helper',    phone: '0800 011 3797',  link: 'https://www.moneyhelper.org.uk' },
    { name: 'Citizens Advice', link: 'https://www.citizensadvice.org.uk' },
    { name: 'StepChange',      phone: '0800 138 1111',  link: 'https://www.stepchange.org' },
  ]},
  { id: 'family',    label: 'Family & Relationships', icon: HeartHandshake, hex: '#F43F5E', items: [
    { name: 'Relate',          phone: '0300 100 1234',  link: 'https://www.relate.org.uk' },
    { name: 'Family Lives',    phone: '0808 800 2222',  link: 'https://www.familylives.org.uk' },
  ]},
  { id: 'physical',  label: 'Physical Health', icon: Activity, hex: '#00E5FF', items: [
    { name: 'NHS Health A-Z',  link: 'https://www.nhs.uk/conditions/' },
    { name: 'Couch to 5K',     link: 'https://www.nhs.uk/live-well/exercise/' },
    { name: 'Sleep Foundation',link: 'https://www.sleepfoundation.org' },
  ]},
];

/* ─────────────────────────  HELPERS  ───────────────────────── */
const todayKey = () => new Date().toISOString().split('T')[0];
const readJSON = (k, fb) => { try { return JSON.parse(localStorage.getItem(k)) ?? fb; } catch { return fb; } };
const writeJSON = (k, v) => localStorage.setItem(k, JSON.stringify(v));

/* ─────────────────────────  COMPONENT  ───────────────────────── */

const WellbeingPageRevamped = ({ getAuthHeaders, currentUser }) => {
  const uname = currentUser?.username || 'anon';
  const today = todayKey();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');

  /* mood */
  const [todayMood, setTodayMood] = useState(null);
  const [moodHistory, setMoodHistory] = useState([]);
  const [selectedMood, setSelectedMood] = useState(3);
  const [energy, setEnergy] = useState(3);
  const [stress, setStress] = useState(3);
  const [moodNote, setMoodNote] = useState('');
  const [showMoodModal, setShowMoodModal] = useState(false);

  /* habits */
  const [habits, setHabits] = useState(DEFAULT_HABITS);
  const [habitProgress, setHabitProgress] = useState({}); // {habitId: number}
  const [streaks, setStreaks] = useState({});
  const [showHabitModal, setShowHabitModal] = useState(false);
  const [editingHabitId, setEditingHabitId] = useState(null);
  const [habitDraft, setHabitDraft] = useState({ label: '', icon: 'Droplets', target: 3, unit: 'times', hex: '#00E5FF' });

  /* gratitude journal */
  const [gratitudeEntries, setGratitudeEntries] = useState([]);
  const [gratitudeToday, setGratitudeToday] = useState(['', '', '']);
  const [showJournalModal, setShowJournalModal] = useState(false);

  /* sleep */
  const [sleepHours, setSleepHours] = useState(null);
  const [sleepInput, setSleepInput] = useState(7.5);
  const [showSleepModal, setShowSleepModal] = useState(false);
  const [sleep7d, setSleep7d] = useState([]);

  /* break timer */
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [breakRunning, setBreakRunning] = useState(false);
  const [breakDuration, setBreakDuration] = useState(300);
  const breakRef = useRef(null);

  /* breathing */
  const [showBreathingModal, setShowBreathingModal] = useState(false);
  const [breathingActive, setBreathingActive] = useState(false);
  const [breathingPattern, setBreathingPattern] = useState(BREATHING_PATTERNS[0]);
  const [breathingPhase, setBreathingPhase] = useState('ready');
  const [breathingCount, setBreathingCount] = useState(0);
  const [breathingCycles, setBreathingCycles] = useState(0);
  const breathingInterval = useRef(null);

  /* ambient */
  const [playing, setPlaying] = useState(null);
  const [volume, setVolume] = useState(0.4);
  const audioRef = useRef(null);

  /* feedback */
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({ category: 'suggestions', message: '', is_anonymous: true });
  const [submitting, setSubmitting] = useState(false);

  /* resources */
  const [expandedResource, setExpandedResource] = useState(null);
  const [resourceSearch, setResourceSearch] = useState('');

  /* ---------- LOAD DATA ---------- */
  useEffect(() => {
    // mood
    const m = readJSON(LS(`mood_${uname}_${today}`), null);
    setTodayMood(m);
    if (m) { setSelectedMood(m.value); setEnergy(m.energy || 3); setStress(m.stress || 3); }

    // 7d mood history
    const hist = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const ds = d.toISOString().split('T')[0];
      const e = readJSON(LS(`mood_${uname}_${ds}`), null);
      hist.push({ date: ds, ...(e || {}) });
    }
    setMoodHistory(hist);

    // habits
    const savedHabits = readJSON(LS(`habits_${uname}`), DEFAULT_HABITS);
    setHabits(savedHabits);
    setHabitProgress(readJSON(LS(`habitProg_${uname}_${today}`), {}));
    setStreaks(readJSON(LS(`habitStreak_${uname}`), {}));

    // gratitude
    setGratitudeEntries(readJSON(LS(`grat_${uname}`), []));
    const gt = readJSON(LS(`grat_${uname}_${today}`), null);
    if (gt) setGratitudeToday(gt);

    // sleep
    setSleepHours(readJSON(LS(`sleep_${uname}_${today}`), null));
    const s7 = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const ds = d.toISOString().split('T')[0];
      s7.push({ date: ds, hours: readJSON(LS(`sleep_${uname}_${ds}`), null) });
    }
    setSleep7d(s7);

    setLoading(false);
  }, [uname, today]);

  /* cleanup break timer & breathing */
  useEffect(() => () => {
    if (breakRef.current) clearInterval(breakRef.current);
    if (breathingInterval.current) clearInterval(breathingInterval.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  /* ---------- MOOD ---------- */
  const submitMood = () => {
    const entry = { value: selectedMood, energy, stress, note: moodNote, date: today, time: new Date().toISOString() };
    writeJSON(LS(`mood_${uname}_${today}`), entry);
    setTodayMood(entry);
    setShowMoodModal(false);
    setMoodNote('');
    const info = MOOD_OPTIONS.find(m => m.value === selectedMood);
    toast.success(`Logged: ${info.label}`, { description: 'Your check-in is saved privately.' });
    // update history
    setMoodHistory(prev => prev.map(h => h.date === today ? { date: today, ...entry } : h));
  };

  /* ---------- HABITS ---------- */
  const bumpHabit = (id, delta = 1) => {
    const cur = habitProgress[id] || 0;
    const habit = habits.find(h => h.id === id);
    const next = Math.max(0, Math.min(habit.target * 2, cur + delta));
    const prog = { ...habitProgress, [id]: next };
    setHabitProgress(prog);
    writeJSON(LS(`habitProg_${uname}_${today}`), prog);

    // if reached target and streak not updated today
    if (next >= habit.target && cur < habit.target) {
      const streak = streaks[id] || { count: 0, last: null };
      const y = new Date(); y.setDate(y.getDate() - 1);
      const yKey = y.toISOString().split('T')[0];
      const newCount = streak.last === yKey ? streak.count + 1 : (streak.last === today ? streak.count : 1);
      const newStreaks = { ...streaks, [id]: { count: newCount, last: today } };
      setStreaks(newStreaks);
      writeJSON(LS(`habitStreak_${uname}`), newStreaks);
      toast.success(`${habit.label} target hit! 🔥 ${newCount} day streak`);
    }
  };

  /* ---------- HABIT CUSTOMISER ---------- */
  const openAddHabit = () => {
    setEditingHabitId(null);
    setHabitDraft({ label: '', icon: 'Droplets', target: 3, unit: 'times', hex: '#00E5FF' });
    setShowHabitModal(true);
  };
  const openEditHabit = (h) => {
    setEditingHabitId(h.id);
    setHabitDraft({ label: h.label, icon: h.icon, target: h.target, unit: h.unit, hex: h.hex });
    setShowHabitModal(true);
  };
  const saveCustomHabit = () => {
    const label = habitDraft.label.trim();
    if (!label) { toast.error('Please give this habit a name'); return; }
    const target = Math.max(1, Math.min(50, parseInt(habitDraft.target) || 1));
    let newHabits;
    if (editingHabitId) {
      newHabits = habits.map(h => h.id === editingHabitId ? { ...h, label, icon: habitDraft.icon, target, unit: habitDraft.unit || 'times', hex: habitDraft.hex } : h);
    } else {
      const id = `custom_${Date.now()}`;
      newHabits = [...habits, { id, label, icon: habitDraft.icon, target, unit: habitDraft.unit || 'times', hex: habitDraft.hex }];
    }
    setHabits(newHabits);
    writeJSON(LS(`habits_${uname}`), newHabits);
    setShowHabitModal(false);
    toast.success(editingHabitId ? 'Habit updated' : 'Habit added');
  };
  const removeHabit = (id) => {
    const remaining = habits.filter(h => h.id !== id);
    setHabits(remaining);
    writeJSON(LS(`habits_${uname}`), remaining);
    // clear progress + streak for that habit
    const { [id]: _p, ...restProg } = habitProgress; setHabitProgress(restProg); writeJSON(LS(`habitProg_${uname}_${today}`), restProg);
    const { [id]: _s, ...restStr } = streaks; setStreaks(restStr); writeJSON(LS(`habitStreak_${uname}`), restStr);
    toast.success('Habit removed');
  };

  /* ---------- GRATITUDE ---------- */
  const saveGratitude = () => {
    const filled = gratitudeToday.filter(x => x.trim());
    if (filled.length === 0) { toast.error('Write at least one thing you are grateful for.'); return; }
    writeJSON(LS(`grat_${uname}_${today}`), gratitudeToday);
    const all = readJSON(LS(`grat_${uname}`), []);
    const existingIdx = all.findIndex(e => e.date === today);
    const newEntry = { date: today, items: filled };
    if (existingIdx >= 0) all[existingIdx] = newEntry; else all.unshift(newEntry);
    writeJSON(LS(`grat_${uname}`), all.slice(0, 30));
    setGratitudeEntries(all.slice(0, 30));
    setShowJournalModal(false);
    toast.success('Gratitude saved 🌱', { description: 'A calmer mind starts with noticing the good.' });
  };

  /* ---------- SLEEP ---------- */
  const saveSleep = () => {
    writeJSON(LS(`sleep_${uname}_${today}`), sleepInput);
    setSleepHours(sleepInput);
    setSleep7d(prev => prev.map(s => s.date === today ? { date: today, hours: sleepInput } : s));
    setShowSleepModal(false);
    toast.success(`Sleep logged: ${sleepInput}h`);
  };

  /* ---------- BREAK TIMER ---------- */
  const startBreak = (secs) => {
    setBreakDuration(secs);
    setBreakSeconds(secs);
    setBreakRunning(true);
    if (breakRef.current) clearInterval(breakRef.current);
    breakRef.current = setInterval(() => {
      setBreakSeconds(s => {
        if (s <= 1) {
          clearInterval(breakRef.current);
          setBreakRunning(false);
          try { new Audio('https://assets.mixkit.co/active_storage/sfx/2354/2354-preview.mp3').play(); } catch {}
          toast.success('Break complete!', { description: 'Welcome back — hydrate & re-focus.' });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };
  const stopBreak = () => { setBreakRunning(false); if (breakRef.current) clearInterval(breakRef.current); };

  /* ---------- BREATHING ---------- */
  const startBreathing = () => {
    setBreathingActive(true); setBreathingCycles(0);
    const p = breathingPattern;
    let phase = 'inhale'; let count = p.inhale;
    setBreathingPhase(phase); setBreathingCount(count);
    if (breathingInterval.current) clearInterval(breathingInterval.current);
    breathingInterval.current = setInterval(() => {
      count--;
      if (count <= 0) {
        if (phase === 'inhale' && p.hold1 > 0) { phase = 'hold'; count = p.hold1; }
        else if ((phase === 'inhale' || phase === 'hold') && p.exhale > 0) { phase = 'exhale'; count = p.exhale; }
        else if (phase === 'exhale' && p.hold2 > 0) { phase = 'hold2'; count = p.hold2; }
        else { phase = 'inhale'; count = p.inhale; setBreathingCycles(c => c + 1); }
        setBreathingPhase(phase);
      }
      setBreathingCount(count);
    }, 1000);
  };
  const stopBreathing = () => {
    setBreathingActive(false); setBreathingPhase('ready'); setBreathingCount(0);
    if (breathingInterval.current) clearInterval(breathingInterval.current);
  };

  /* ---------- AMBIENT ---------- */
  const toggleSound = (s) => {
    if (playing === s.name) {
      if (audioRef.current) { audioRef.current.pause(); }
      setPlaying(null); return;
    }
    if (audioRef.current) audioRef.current.pause();
    const a = new Audio(s.url); a.loop = true; a.volume = volume;
    audioRef.current = a;
    a.play().catch(() => toast.error('Could not play audio (network blocked?)'));
    setPlaying(s.name);
  };
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume]);

  /* ---------- FEEDBACK ---------- */
  const submitFeedback = async () => {
    if (!feedbackData.message.trim()) { toast.error('Please enter your feedback'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/wellbeing/feedback`, feedbackData, { headers: getAuthHeaders() });
      toast.success('Feedback submitted', { description: 'Thank you for sharing.' });
      setShowFeedbackModal(false);
      setFeedbackData({ category: 'suggestions', message: '', is_anonymous: true });
    } catch { toast.error('Failed to submit feedback'); }
    finally { setSubmitting(false); }
  };

  /* ─────────  DERIVED  ───────── */
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return { text: 'Good morning', icon: Sun,  hex: '#F59E0B' };
    if (h < 17) return { text: 'Good afternoon', icon: Sun, hex: '#F97316' };
    return { text: 'Good evening', icon: Moon, hex: '#8A2BE2' };
  }, []);

  const wellnessScore = useMemo(() => {
    let score = 40; // baseline
    if (todayMood) score += todayMood.value * 4; // up to +20
    if (todayMood) score += (todayMood.energy || 3) * 2; // up to +10
    if (todayMood) score -= ((todayMood.stress || 3) - 3) * 3; // up to +/-6
    if (sleepHours) score += Math.min(10, Math.max(0, (sleepHours - 5) * 2));
    // habits
    const hitCount = habits.filter(h => (habitProgress[h.id] || 0) >= h.target).length;
    score += hitCount * 5;
    // clamp
    score = Math.max(0, Math.min(100, Math.round(score)));
    return score;
  }, [todayMood, sleepHours, habitProgress, habits]);

  const scoreBand = wellnessScore >= 80 ? { label: 'Thriving', hex: '#10B981' }
                : wellnessScore >= 60 ? { label: 'Steady',   hex: '#00E5FF' }
                : wellnessScore >= 40 ? { label: 'Manageable', hex: '#8A2BE2' }
                : wellnessScore >= 20 ? { label: 'Low', hex: '#F97316' }
                : { label: 'Needs care', hex: '#F43F5E' };

  const avgMood = useMemo(() => {
    const vals = moodHistory.filter(m => m.value).map(m => m.value);
    return vals.length ? (vals.reduce((a,b) => a+b, 0) / vals.length).toFixed(1) : '—';
  }, [moodHistory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="wellbeing-loading">
        <div className="w-10 h-10 border-2 border-[#00E5FF]/30 border-t-[#00E5FF] rounded-full animate-spin" />
      </div>
    );
  }

  const mm = (s) => `${Math.floor(s/60).toString().padStart(1,'0')}:${(s%60).toString().padStart(2,'0')}`;

  const TABS = [
    { id: 'today',     label: 'Today',     icon: Compass },
    { id: 'habits',    label: 'Habits',    icon: Target },
    { id: 'mind',      label: 'Mind',      icon: Brain },
    { id: 'sleep',     label: 'Sleep',     icon: Moon },
    { id: 'support',   label: 'Support',   icon: Shield },
  ];

  return (
    <div className="space-y-6" data-testid="wellbeing-page">
      {/* ═══ HERO ═══ */}
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] via-[#0b0a15] to-[#0a0d1a] p-6 lg:p-8">
        {/* Ambient glow */}
        <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        <div className="relative flex flex-col lg:flex-row items-start lg:items-center gap-6">
          {/* Greeting */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-[0.2em]">
              <greeting.icon className="w-3.5 h-3.5" style={{ color: greeting.hex }} />
              {greeting.text}
            </div>
            <h1 className="mt-2 text-3xl lg:text-4xl font-light text-white">
              How are you <span className="italic font-serif" style={{ color: '#00E5FF' }}>really</span> doing?
            </h1>
            <p className="mt-2 text-white/40 text-sm max-w-xl">
              Your check-ins stay on this device — private, judgement free, and yours alone.
              A quick two-minute pause here can change your whole day.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                onClick={() => setShowMoodModal(true)}
                data-testid="checkin-btn"
                className="bg-gradient-to-r from-[#00E5FF]/20 to-[#8A2BE2]/20 border border-[#00E5FF]/30 text-white hover:from-[#00E5FF]/30 hover:to-[#8A2BE2]/30 rounded-full px-5"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {todayMood ? 'Update check-in' : 'Start check-in'}
              </Button>
              <Button
                onClick={() => setShowBreathingModal(true)}
                variant="outline"
                data-testid="breathe-btn"
                className="bg-white/[0.02] border-white/10 text-white/80 hover:bg-white/[0.05] rounded-full px-5"
              >
                <Wind className="w-4 h-4 mr-2" />
                Breathe
              </Button>
              <Button
                onClick={() => setShowFeedbackModal(true)}
                variant="outline"
                data-testid="feedback-btn"
                className="bg-white/[0.02] border-white/10 text-white/80 hover:bg-white/[0.05] rounded-full px-5"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Anon feedback
              </Button>
            </div>
          </div>

          {/* Wellness Score Ring */}
          <div className="w-full lg:w-auto flex items-center justify-center">
            <div className="relative w-44 h-44">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <defs>
                  <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%"  stopColor="#00E5FF" />
                    <stop offset="100%" stopColor="#8A2BE2" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="42" stroke="rgba(255,255,255,0.06)" strokeWidth="6" fill="none" />
                <circle
                  cx="50" cy="50" r="42"
                  stroke="url(#scoreGrad)"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${(wellnessScore / 100) * 264} 264`}
                  strokeLinecap="round"
                  style={{ transition: 'stroke-dasharray 800ms ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Wellness</div>
                <div className="text-5xl font-light text-white">{wellnessScore}</div>
                <div className="text-xs mt-1" style={{ color: scoreBand.hex }}>{scoreBand.label}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ EMERGENCY STRIP ═══ */}
      <Card className="bg-gradient-to-r from-[#F43F5E]/[0.08] via-[#F97316]/[0.05] to-transparent border-[#F43F5E]/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-xl bg-[#F43F5E]/15 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-[#F43F5E]" />
              </div>
              <div>
                <div className="text-white/90 text-sm font-medium">In crisis right now?</div>
                <div className="text-white/40 text-xs">You are not alone — free, confidential help is one tap away.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {CRISIS.map(c => (
                <a
                  key={c.name}
                  href={c.phone.includes('Text') ? undefined : `tel:${c.phone.replace(/\s/g,'')}`}
                  data-testid={`crisis-${c.name.toLowerCase().replace(/\s/g,'-')}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                    c.urgent ? 'bg-[#F43F5E]/15 border-[#F43F5E]/30 text-[#fca5a5]' : 'bg-white/[0.03] border-white/10 text-white/70 hover:bg-white/[0.06]'
                  }`}
                >
                  <Phone className="w-3 h-3" />
                  {c.name} • {c.phone}
                </a>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══ TABS ═══ */}
      <div className="flex gap-1 p-1 bg-white/[0.02] border border-white/[0.06] rounded-2xl w-fit overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === t.id
                ? 'bg-gradient-to-r from-[#00E5FF]/20 to-[#8A2BE2]/20 text-white border border-white/10'
                : 'text-white/50 hover:text-white/90 hover:bg-white/[0.03]'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ═════════════════  TODAY TAB  ═════════════════ */}
      {activeTab === 'today' && (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Check-in status */}
          <Card className="lg:col-span-2 bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">Today's check-in</div>
                  <div className="text-white text-lg font-medium mt-1">
                    {todayMood ? `You feel ${MOOD_OPTIONS.find(m => m.value === todayMood.value)?.label}` : 'Not yet logged'}
                  </div>
                </div>
                <Button onClick={() => setShowMoodModal(true)} variant="outline" size="sm" className="bg-white/[0.02] border-white/10 text-white/80">
                  {todayMood ? 'Update' : 'Log now'}
                </Button>
              </div>
              {todayMood ? (
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Mood',   value: todayMood.value,           icon: Heart,   hex: MOOD_OPTIONS.find(m => m.value === todayMood.value)?.hex || '#00E5FF' },
                    { label: 'Energy', value: todayMood.energy || 3,     icon: Battery, hex: '#10B981' },
                    { label: 'Stress', value: todayMood.stress || 3,     icon: Activity,hex: '#F97316' },
                  ].map(m => (
                    <div key={m.label} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/40 uppercase tracking-wider">{m.label}</span>
                        <m.icon className="w-3.5 h-3.5" style={{ color: m.hex }} />
                      </div>
                      <div className="mt-2 flex items-end justify-between">
                        <div className="text-2xl font-light text-white">{m.value}</div>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(i => (
                            <div key={i} className="w-1.5 h-4 rounded-full" style={{ background: i <= m.value ? m.hex : 'rgba(255,255,255,0.08)' }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-white/40 text-sm">Log your first check-in to unlock trends, streaks and a personalised wellness score.</div>
              )}
              {todayMood?.note && (
                <div className="mt-4 p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] text-white/70 text-sm italic">"{todayMood.note}"</div>
              )}
            </CardContent>
          </Card>

          {/* 7d Mood chart */}
          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">7-day mood</div>
                <Badge className="bg-white/[0.04] border-white/10 text-white/60">avg {avgMood}</Badge>
              </div>
              <div className="flex items-end justify-between h-32 gap-1">
                {moodHistory.map(m => {
                  const info = m.value ? MOOD_OPTIONS.find(x => x.value === m.value) : null;
                  const h = m.value ? (m.value / 5) * 100 : 6;
                  return (
                    <div key={m.date} className="flex-1 flex flex-col items-center gap-2">
                      <div className="w-full rounded-t-md" style={{ height: `${h}%`, background: info ? `linear-gradient(to top, ${info.hex}, ${info.hex}88)` : 'rgba(255,255,255,0.05)' }} />
                      <div className="text-[10px] text-white/40">{new Date(m.date).toLocaleDateString('en-GB', { weekday: 'short' }).slice(0,1)}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Quick pause buttons */}
          <Card className="lg:col-span-3 bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">Quick pause</div>
                  <div className="text-white text-base font-medium mt-1">
                    {breakRunning ? <span className="font-mono text-[#00E5FF]">{mm(breakSeconds)} remaining</span> : 'Take a short reset'}
                  </div>
                </div>
                {breakRunning ? (
                  <Button onClick={stopBreak} size="sm" variant="outline" className="bg-white/[0.02] border-white/10 text-white/80">
                    <Pause className="w-3.5 h-3.5 mr-1.5" /> Stop
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Micro',   sub: '2 min',  secs: 120, hex: '#00E5FF', icon: Zap },
                  { label: 'Coffee',  sub: '5 min',  secs: 300, hex: '#F59E0B', icon: Coffee },
                  { label: 'Walk',    sub: '10 min', secs: 600, hex: '#10B981', icon: Footprints },
                  { label: 'Reset',   sub: '15 min', secs: 900, hex: '#8A2BE2', icon: Compass },
                ].map(b => (
                  <button
                    key={b.label}
                    onClick={() => startBreak(b.secs)}
                    disabled={breakRunning}
                    data-testid={`break-${b.label.toLowerCase()}`}
                    className="group p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-white/15 disabled:opacity-40 transition-all text-left"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${b.hex}18` }}>
                        <b.icon className="w-4.5 h-4.5" style={{ color: b.hex }} />
                      </div>
                      <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/60" />
                    </div>
                    <div className="text-white text-sm font-medium">{b.label}</div>
                    <div className="text-white/40 text-xs">{b.sub}</div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═════════════════  HABITS TAB  ═════════════════ */}
      {activeTab === 'habits' && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/40">Your habits</div>
              <div className="text-white text-lg font-medium mt-0.5">Build the day you want, one small win at a time</div>
            </div>
            <Button onClick={openAddHabit} data-testid="habit-add-btn" className="bg-gradient-to-r from-[#00E5FF]/25 to-[#8A2BE2]/25 border border-[#00E5FF]/30 text-white rounded-full">
              <Plus className="w-4 h-4 mr-1.5" /> Add habit
            </Button>
          </div>
          <div className="grid lg:grid-cols-2 gap-5">
          {habits.map(h => {
            const Icon = HABIT_ICONS[h.icon] || Droplets;
            const cur = habitProgress[h.id] || 0;
            const pct = Math.min(100, (cur / h.target) * 100);
            const done = cur >= h.target;
            const streak = streaks[h.id]?.count || 0;
            return (
              <Card key={h.id} className="bg-white/[0.02] border-white/[0.06] overflow-hidden group">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${h.hex}15`, boxShadow: `0 0 24px ${h.hex}18` }}>
                        <Icon className="w-5 h-5" style={{ color: h.hex }} />
                      </div>
                      <div>
                        <div className="text-white font-medium">{h.label}</div>
                        <div className="text-white/40 text-xs">Target: {h.target} {h.unit}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {streak > 0 && (
                        <Badge className="bg-[#F97316]/10 border-[#F97316]/30 text-[#fdba74]">
                          <Flame className="w-3 h-3 mr-1" /> {streak}
                        </Badge>
                      )}
                      <button onClick={() => openEditHabit(h)} data-testid={`habit-${h.id}-edit`} className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-white/80 hover:bg-white/[0.05] transition-all opacity-0 group-hover:opacity-100">
                        <Feather className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeHabit(h.id)} data-testid={`habit-${h.id}-remove`} className="w-7 h-7 rounded-lg flex items-center justify-center text-white/30 hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 transition-all opacity-0 group-hover:opacity-100">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-white text-2xl font-light">{cur}<span className="text-white/40 text-sm ml-1">/ {h.target}</span></div>
                      {done && <CheckCircle className="w-5 h-5 text-emerald-400" />}
                    </div>
                    <div className="w-full h-2 rounded-full bg-white/[0.04] overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${h.hex}, ${h.hex}bb)` }} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button onClick={() => bumpHabit(h.id, -1)} variant="outline" size="sm" className="bg-white/[0.02] border-white/10 text-white/70" disabled={cur === 0}>
                      <span className="text-lg leading-none">−</span>
                    </Button>
                    <Button
                      onClick={() => bumpHabit(h.id, 1)}
                      data-testid={`habit-${h.id}-add`}
                      className="flex-1 border rounded-lg text-white/90 hover:opacity-90"
                      style={{ background: `${h.hex}18`, borderColor: `${h.hex}35` }}
                    >
                      <Plus className="w-4 h-4 mr-1.5" /> Log +1
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Add-habit card */}
          <button
            onClick={openAddHabit}
            data-testid="habit-add-card"
            className="group rounded-xl border border-dashed border-white/10 hover:border-[#00E5FF]/40 bg-white/[0.01] hover:bg-white/[0.03] transition-all flex flex-col items-center justify-center min-h-[180px] gap-2 text-white/40 hover:text-white"
          >
            <div className="w-11 h-11 rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center group-hover:border-[#00E5FF]/40 group-hover:bg-[#00E5FF]/10 transition-all">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-sm font-medium">Add a habit</div>
            <div className="text-xs text-white/30">Icon · target · colour</div>
          </button>
          </div>

          {/* Water visual (only if water habit exists) */}
          {habits.some(h => h.id === 'water') && (
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-white/40">Hydration today</div>
                    <div className="text-white text-lg font-medium mt-1">{habitProgress.water || 0} of {habits.find(h => h.id === 'water')?.target || 8} glasses</div>
                  </div>
                  <Droplets className="w-5 h-5 text-[#00E5FF]" />
                </div>
                <div className="grid grid-cols-8 gap-2">
                  {Array.from({ length: habits.find(h => h.id === 'water')?.target || 8 }).map((_, i) => {
                    const filled = (habitProgress.water || 0) > i;
                    return (
                      <button
                        key={i}
                        onClick={() => bumpHabit('water', filled && (habitProgress.water || 0) === i + 1 ? -1 : 1)}
                        data-testid={`water-glass-${i}`}
                        className="aspect-[3/4] rounded-lg border transition-all relative overflow-hidden"
                        style={{
                          borderColor: filled ? '#00E5FF55' : 'rgba(255,255,255,0.08)',
                          background: filled
                            ? 'linear-gradient(to top, #00E5FF33 0%, #00E5FF11 100%)'
                            : 'rgba(255,255,255,0.02)',
                        }}
                      >
                        {filled && <Droplets className="absolute inset-0 m-auto w-4 h-4 text-[#00E5FF]" />}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ═════════════════  MIND TAB  ═════════════════ */}
      {activeTab === 'mind' && (
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Breathing card */}
          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">Guided breathing</div>
                  <div className="text-white text-lg font-medium mt-1">Reset in under 3 minutes</div>
                </div>
                <Wind className="w-5 h-5 text-[#8A2BE2]" />
              </div>
              <div className="space-y-2">
                {BREATHING_PATTERNS.map(p => (
                  <button
                    key={p.name}
                    onClick={() => { setBreathingPattern(p); setShowBreathingModal(true); }}
                    data-testid={`breathe-${p.name.toLowerCase().replace(/[^a-z]/g,'-')}`}
                    className="w-full p-3 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-[#8A2BE2]/30 transition-all flex items-center justify-between text-left group"
                  >
                    <div>
                      <div className="text-white text-sm font-medium">{p.name}</div>
                      <div className="text-white/40 text-xs">{p.desc}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#8A2BE2]" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Ambient sounds */}
          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">Ambient soundscapes</div>
                  <div className="text-white text-lg font-medium mt-1">{playing ? `Now playing: ${playing}` : 'Choose your calm'}</div>
                </div>
                {playing && (
                  <button onClick={() => toggleSound({ name: playing })} className="text-white/60 hover:text-white">
                    <Pause className="w-5 h-5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {AMBIENT_SOUNDS.map(s => {
                  const active = playing === s.name;
                  return (
                    <button
                      key={s.name}
                      onClick={() => toggleSound(s)}
                      data-testid={`sound-${s.name.toLowerCase().replace(/\s/g,'-')}`}
                      className={`aspect-square rounded-xl border transition-all flex flex-col items-center justify-center gap-2 ${
                        active ? 'border-white/20' : 'border-white/[0.06] hover:border-white/10'
                      }`}
                      style={{ background: active ? `${s.hex}18` : 'rgba(255,255,255,0.02)' }}
                    >
                      <s.icon className="w-6 h-6" style={{ color: active ? s.hex : 'rgba(255,255,255,0.5)' }} />
                      <span className="text-xs" style={{ color: active ? s.hex : 'rgba(255,255,255,0.5)' }}>{s.name}</span>
                    </button>
                  );
                })}
              </div>
              {playing && (
                <div className="mt-4 flex items-center gap-3">
                  <VolumeX className="w-4 h-4 text-white/40" />
                  <input
                    type="range" min="0" max="1" step="0.05" value={volume}
                    onChange={(e) => setVolume(parseFloat(e.target.value))}
                    className="flex-1 accent-[#00E5FF]"
                  />
                  <Volume2 className="w-4 h-4 text-white/40" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Gratitude Journal */}
          <Card className="lg:col-span-2 bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">Gratitude journal</div>
                  <div className="text-white text-lg font-medium mt-1">Three good things today</div>
                </div>
                <Button onClick={() => setShowJournalModal(true)} size="sm" data-testid="journal-open" className="bg-white/[0.02] border border-white/10 text-white/80 hover:bg-white/[0.05]">
                  <Feather className="w-3.5 h-3.5 mr-1.5" /> Write today
                </Button>
              </div>
              {gratitudeEntries.length === 0 ? (
                <div className="p-6 rounded-xl bg-white/[0.02] border border-dashed border-white/[0.08] text-center">
                  <BookHeart className="w-6 h-6 mx-auto text-white/30 mb-2" />
                  <div className="text-white/50 text-sm">No entries yet. A daily practice of noticing 3 good things is one of the most researched anti-anxiety habits.</div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-72 overflow-y-auto">
                  {gratitudeEntries.slice(0, 12).map(e => (
                    <div key={e.date} className="p-4 rounded-xl bg-gradient-to-br from-[#00E5FF]/[0.04] to-[#8A2BE2]/[0.04] border border-white/[0.06]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-white/40 text-[10px] uppercase tracking-wider">
                          {new Date(e.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </div>
                        <Quote className="w-3 h-3 text-white/20" />
                      </div>
                      <ul className="space-y-1.5">
                        {e.items.map((it, i) => (
                          <li key={i} className="text-white/80 text-xs flex items-start gap-2">
                            <span className="text-[#00E5FF] mt-0.5">◆</span>
                            <span>{it}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═════════════════  SLEEP TAB  ═════════════════ */}
      {activeTab === 'sleep' && (
        <div className="grid lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2 bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/40">Sleep last night</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-4xl font-light text-white">{sleepHours ?? '—'}</span>
                    {sleepHours && <span className="text-white/40 text-sm">hours</span>}
                  </div>
                </div>
                <Button onClick={() => { setSleepInput(sleepHours || 7.5); setShowSleepModal(true); }} size="sm" data-testid="sleep-log" className="bg-white/[0.02] border border-white/10 text-white/80 hover:bg-white/[0.05]">
                  <Moon className="w-3.5 h-3.5 mr-1.5" /> {sleepHours ? 'Update' : 'Log sleep'}
                </Button>
              </div>
              <div className="grid grid-cols-7 gap-2 items-end h-40 mt-6">
                {sleep7d.map(s => {
                  const h = s.hours || 0;
                  const pct = Math.min(100, (h / 10) * 100);
                  const color = h >= 7 ? '#10B981' : h >= 5 ? '#F59E0B' : h > 0 ? '#F43F5E' : 'rgba(255,255,255,0.05)';
                  return (
                    <div key={s.date} className="flex flex-col items-center gap-2">
                      <div className="w-full rounded-t-md flex items-end justify-center relative" style={{ height: `${Math.max(4, pct)}%`, background: `linear-gradient(to top, ${color}, ${color}88)` }}>
                        {h > 0 && <span className="absolute -top-5 text-[10px] text-white/60">{h}h</span>}
                      </div>
                      <div className="text-[10px] text-white/40">{new Date(s.date).toLocaleDateString('en-GB', { weekday: 'short' }).slice(0,1)}</div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-white/40 mb-3">Better sleep tonight</div>
              <ul className="space-y-3 text-sm">
                {[
                  { i: Moon, t: 'Screens off 60 min before bed' },
                  { i: Coffee, t: 'No caffeine after 2 PM' },
                  { i: Bed, t: 'Cool, dark, quiet room' },
                  { i: Wind, t: '4-7-8 breathing before sleep' },
                  { i: Sun, t: '10 min sunlight in the morning' },
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70">
                    <tip.i className="w-4 h-4 text-[#8A2BE2] mt-0.5 flex-shrink-0" />
                    <span>{tip.t}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═════════════════  SUPPORT TAB  ═════════════════ */}
      {activeTab === 'support' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <input
              type="text"
              value={resourceSearch}
              onChange={(e) => setResourceSearch(e.target.value)}
              placeholder="Search resources..."
              className="w-full bg-white/[0.02] border border-white/[0.06] rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#00E5FF]/30"
              data-testid="resource-search"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {RESOURCE_GROUPS
              .filter(g => !resourceSearch || g.label.toLowerCase().includes(resourceSearch.toLowerCase()) || g.items.some(i => i.name.toLowerCase().includes(resourceSearch.toLowerCase())))
              .map(g => {
              const isOpen = expandedResource === g.id;
              return (
                <Card key={g.id} className={`bg-white/[0.02] border-white/[0.06] ${isOpen ? 'lg:col-span-2' : ''}`}>
                  <button
                    onClick={() => setExpandedResource(isOpen ? null : g.id)}
                    className="w-full p-5 flex items-center justify-between text-left"
                    data-testid={`resource-${g.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${g.hex}15` }}>
                        <g.icon className="w-5 h-5" style={{ color: g.hex }} />
                      </div>
                      <div>
                        <div className="text-white font-medium">{g.label}</div>
                        <div className="text-white/40 text-xs">{g.items.length} resources</div>
                      </div>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 space-y-2 border-t border-white/[0.04] pt-4">
                      {g.items.map(item => (
                        <div key={item.name} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.04] flex items-center justify-between">
                          <div className="min-w-0">
                            <div className="text-white text-sm font-medium">{item.name}</div>
                            {item.phone && (
                              <a href={`tel:${item.phone.replace(/\s/g,'')}`} className="text-[#00E5FF] text-xs inline-flex items-center gap-1 mt-1">
                                <Phone className="w-3 h-3" /> {item.phone}
                              </a>
                            )}
                          </div>
                          {item.link && (
                            <a href={item.link} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white/60 hover:text-white hover:border-white/15">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {/* People contact */}
          <Card className="bg-gradient-to-r from-[#00E5FF]/[0.04] to-[#8A2BE2]/[0.04] border-white/[0.06]">
            <CardContent className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#00E5FF]/15 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-[#00E5FF]" />
                </div>
                <div>
                  <div className="text-white font-medium">Talk to our People team</div>
                  <div className="text-white/40 text-sm">Confidential, no judgment, always here to listen.</div>
                </div>
              </div>
              <a
                href="mailto:people@starnightholdings.uk"
                data-testid="people-team-email"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.03] border border-white/10 text-white/90 hover:bg-white/[0.06] transition-all text-sm"
              >
                <Mail className="w-4 h-4" />
                people@starnightholdings.uk
              </a>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ═════════════════  MOOD MODAL  ═════════════════ */}
      {/* Mood Modal — Jewel */}
      <JewelModal
        open={showMoodModal}
        onOpenChange={setShowMoodModal}
        icon={Sparkles}
        title="How are you today?"
        subtitle="A quick check-in kept private on your device"
        accent="cyan"
        size="md"
        testid="mood-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowMoodModal(false)}>Cancel</JewelButton>
          <JewelButton accent="cyan" onClick={submitMood} icon={Check} data-testid="mood-save">Save check-in</JewelButton>
        </>}
      >
        <div className="space-y-5">
          <JewelField label="Mood">
            <div className="grid grid-cols-5 gap-2">
              {MOOD_OPTIONS.map(m => (
                <button
                  key={m.value}
                  onClick={() => setSelectedMood(m.value)}
                  data-testid={`mood-${m.value}`}
                  className="p-3 rounded-xl flex flex-col items-center gap-1.5 transition-all border"
                  style={{
                    background: selectedMood === m.value ? `${m.hex}18` : 'rgba(255,255,255,0.02)',
                    borderColor: selectedMood === m.value ? `${m.hex}55` : 'rgba(255,255,255,0.06)',
                    boxShadow: selectedMood === m.value ? `0 0 20px -6px ${m.hex}80` : 'none',
                  }}
                >
                  <m.icon className="w-5 h-5" style={{ color: selectedMood === m.value ? m.hex : 'rgba(255,255,255,0.5)' }} />
                  <span className="text-[10px] font-medium" style={{ color: selectedMood === m.value ? m.hex : 'rgba(255,255,255,0.55)' }}>{m.label}</span>
                </button>
              ))}
            </div>
          </JewelField>

          {[
            { label: 'Energy', value: energy, set: setEnergy, hex: '#10B981' },
            { label: 'Stress', value: stress, set: setStress, hex: '#F97316' },
          ].map(s => (
            <JewelField key={s.label} label={<>{s.label} <span className="text-white/40 font-mono ml-1">{s.value}/5</span></>}>
              <input
                type="range" min="1" max="5" value={s.value}
                onChange={(e) => s.set(parseInt(e.target.value))}
                className="w-full"
                style={{ accentColor: s.hex }}
              />
            </JewelField>
          ))}

          <JewelField label="A note (optional)">
            <JewelTextarea
              value={moodNote}
              onChange={(e) => setMoodNote(e.target.value)}
              rows={2}
              placeholder="Anything on your mind…"
            />
          </JewelField>
        </div>
      </JewelModal>

      {/* ═════════════════  BREATHING MODAL  ═════════════════ */}
      <Dialog open={showBreathingModal} onOpenChange={(o) => { if (!o) stopBreathing(); setShowBreathingModal(o); }}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wind className="w-5 h-5 text-[#8A2BE2]" />
              {breathingPattern.name}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex justify-center py-6">
              <div
                className="relative w-48 h-48 rounded-full transition-all duration-1000 ease-in-out flex items-center justify-center"
                style={{
                  background: 'radial-gradient(circle, rgba(138,43,226,0.25) 0%, rgba(0,229,255,0.05) 70%, transparent 100%)',
                  transform: breathingPhase === 'inhale' ? 'scale(1.35)' : breathingPhase === 'exhale' ? 'scale(0.7)' : 'scale(1)',
                  boxShadow: '0 0 60px rgba(138,43,226,0.35)',
                }}
              >
                <div className="text-center">
                  <div className="text-white/50 uppercase tracking-widest text-xs">{breathingPhase === 'ready' ? 'Get ready' : breathingPhase}</div>
                  <div className="text-white text-5xl font-light mt-1">{breathingCount || breathingPattern.inhale}</div>
                  {breathingCycles > 0 && <div className="text-white/40 text-xs mt-2">Cycle {breathingCycles}</div>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {BREATHING_PATTERNS.map(p => (
                <button
                  key={p.name}
                  onClick={() => { if (!breathingActive) setBreathingPattern(p); }}
                  disabled={breathingActive}
                  className={`p-3 rounded-xl border text-left text-xs transition-all ${
                    breathingPattern.name === p.name ? 'border-[#8A2BE2]/50 bg-[#8A2BE2]/10' : 'border-white/[0.06] bg-white/[0.02]'
                  }`}
                >
                  <div className="text-white text-sm">{p.name}</div>
                  <div className="text-white/40">{p.desc}</div>
                </button>
              ))}
            </div>
            <Button
              onClick={breathingActive ? stopBreathing : startBreathing}
              data-testid="breathing-toggle"
              className="w-full bg-gradient-to-r from-[#8A2BE2]/30 to-[#00E5FF]/30 border border-[#8A2BE2]/30 text-white"
            >
              {breathingActive ? <><Pause className="w-4 h-4 mr-2" /> Stop</> : <><Play className="w-4 h-4 mr-2" /> Begin</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Journal Modal — Jewel */}
      <JewelModal
        open={showJournalModal}
        onOpenChange={setShowJournalModal}
        icon={BookHeart}
        title="Three Good Things"
        subtitle="Even the smallest ones count · kept private on your device"
        accent="cyan"
        size="md"
        testid="journal-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowJournalModal(false)}>Cancel</JewelButton>
          <JewelButton accent="cyan" onClick={saveGratitude} icon={Check} data-testid="grat-save">Save today</JewelButton>
        </>}
      >
        <div className="space-y-3">
          {gratitudeToday.map((val, i) => (
            <div key={i} className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 border border-white/10 flex items-center justify-center text-[10px] font-medium text-white/80 pointer-events-none">{i + 1}</div>
              <JewelInput
                value={val}
                onChange={(e) => { const c = [...gratitudeToday]; c[i] = e.target.value; setGratitudeToday(c); }}
                placeholder={`Something you're grateful for…`}
                data-testid={`grat-input-${i}`}
                className="pl-12"
              />
            </div>
          ))}
        </div>
      </JewelModal>

      {/* Sleep Modal — Jewel */}
      <JewelModal
        open={showSleepModal}
        onOpenChange={setShowSleepModal}
        icon={Moon}
        title="Sleep Log"
        subtitle="How did last night go?"
        accent="violet"
        size="md"
        testid="sleep-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowSleepModal(false)}>Cancel</JewelButton>
          <JewelButton accent="violet" onClick={saveSleep} icon={Moon} data-testid="sleep-save">Log sleep</JewelButton>
        </>}
      >
        <div className="space-y-5">
          <div className="relative p-6 rounded-2xl overflow-hidden border border-white/[0.06]"
            style={{ background: 'radial-gradient(circle at 50% 0%, rgba(138,43,226,0.15) 0%, transparent 70%)' }}>
            <div className="text-center relative z-10">
              <div className="text-7xl font-extralight text-white leading-none">
                {sleepInput}<span className="text-white/30 text-3xl ml-1 font-light">h</span>
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-3">hours slept last night</div>
              {/* Quality indicator */}
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.06]">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: sleepInput >= 7 && sleepInput <= 9 ? '#10B981' : sleepInput >= 5 ? '#F59E0B' : '#F43F5E' }} />
                <span className="text-[10px] text-white/60 tracking-wide">{sleepInput >= 7 && sleepInput <= 9 ? 'Restful range' : sleepInput >= 5 ? 'A bit short' : 'Under-slept'}</span>
              </div>
            </div>
          </div>
          <input
            type="range" min="0" max="12" step="0.5" value={sleepInput}
            onChange={(e) => setSleepInput(parseFloat(e.target.value))}
            className="w-full accent-[#8A2BE2]"
          />
          <div className="flex justify-between text-[10px] text-white/30 tracking-wider">
            <span>0h</span><span>4h</span><span>8h</span><span>12h</span>
          </div>
        </div>
      </JewelModal>

      {/* ═════════════════  HABIT MODAL  ═════════════════ */}
      <Dialog open={showHabitModal} onOpenChange={setShowHabitModal}>
        <DialogContent className="bg-[#0a0d1a] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#00E5FF]" />
              {editingHabitId ? 'Edit habit' : 'Add a habit'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Name</div>
              <input
                type="text"
                value={habitDraft.label}
                onChange={(e) => setHabitDraft({ ...habitDraft, label: e.target.value })}
                placeholder="e.g. Read 10 pages"
                data-testid="habit-label-input"
                maxLength={40}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:border-[#00E5FF]/30"
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Icon</div>
              <div className="grid grid-cols-5 gap-2">
                {HABIT_ICON_KEYS.map(k => {
                  const Icon = HABIT_ICONS[k];
                  const active = habitDraft.icon === k;
                  return (
                    <button
                      key={k}
                      onClick={() => setHabitDraft({ ...habitDraft, icon: k })}
                      data-testid={`habit-icon-${k}`}
                      className="aspect-square rounded-xl border flex items-center justify-center transition-all"
                      style={{
                        background: active ? `${habitDraft.hex}20` : 'rgba(255,255,255,0.02)',
                        borderColor: active ? `${habitDraft.hex}55` : 'rgba(255,255,255,0.06)',
                      }}
                    >
                      <Icon className="w-5 h-5" style={{ color: active ? habitDraft.hex : 'rgba(255,255,255,0.5)' }} />
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Colour</div>
              <div className="flex gap-2">
                {HABIT_HEXES.map(hex => (
                  <button
                    key={hex}
                    onClick={() => setHabitDraft({ ...habitDraft, hex })}
                    data-testid={`habit-color-${hex.slice(1)}`}
                    className="w-9 h-9 rounded-full border-2 transition-all"
                    style={{
                      background: hex,
                      borderColor: habitDraft.hex === hex ? '#fff' : 'transparent',
                      boxShadow: habitDraft.hex === hex ? `0 0 12px ${hex}88` : 'none',
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Target / day</div>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={habitDraft.target}
                  onChange={(e) => setHabitDraft({ ...habitDraft, target: e.target.value })}
                  data-testid="habit-target-input"
                  className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:border-[#00E5FF]/30"
                />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-white/50 mb-2">Unit</div>
                <input
                  type="text"
                  value={habitDraft.unit}
                  onChange={(e) => setHabitDraft({ ...habitDraft, unit: e.target.value })}
                  placeholder="times, pages…"
                  data-testid="habit-unit-input"
                  maxLength={20}
                  className="w-full px-4 py-3 bg-white/[0.02] border border-white/[0.06] rounded-xl text-white text-sm focus:outline-none focus:border-[#00E5FF]/30"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${habitDraft.hex}15`, boxShadow: `0 0 24px ${habitDraft.hex}18` }}>
                {React.createElement(HABIT_ICONS[habitDraft.icon] || Droplets, { className: 'w-5 h-5', style: { color: habitDraft.hex } })}
              </div>
              <div>
                <div className="text-white text-sm font-medium">{habitDraft.label || 'Your habit'}</div>
                <div className="text-white/40 text-xs">Target: {habitDraft.target || 1} {habitDraft.unit || 'times'}</div>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Button onClick={() => setShowHabitModal(false)} variant="outline" className="flex-1 bg-white/[0.02] border-white/10 text-white/70">Cancel</Button>
              <Button onClick={saveCustomHabit} data-testid="habit-save" className="flex-1 bg-gradient-to-r from-[#00E5FF]/30 to-[#8A2BE2]/30 border border-[#00E5FF]/30 text-white">
                {editingHabitId ? 'Save changes' : 'Add habit'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal — Jewel */}
      <JewelModal
        open={showFeedbackModal}
        onOpenChange={setShowFeedbackModal}
        icon={MessageCircle}
        title="Share Confidentially"
        subtitle="Your feedback lands directly with the People team"
        accent="cyan"
        size="md"
        testid="feedback-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowFeedbackModal(false)}>Cancel</JewelButton>
          <JewelButton accent="cyan" onClick={submitFeedback} disabled={submitting} loading={submitting} icon={Send} data-testid="feedback-submit">Submit</JewelButton>
        </>}
      >
        <div className="space-y-4">
          <JewelField label="Category">
            <div className="grid grid-cols-3 gap-2">
              {['workload', 'environment', 'support', 'suggestions', 'other'].map(c => {
                const active = feedbackData.category === c;
                return (
                  <button
                    key={c}
                    onClick={() => setFeedbackData({ ...feedbackData, category: c })}
                    className={`p-2 rounded-xl text-xs border transition-all ${
                      active
                        ? 'bg-cyan-500/15 border-cyan-400/40 text-cyan-300'
                        : 'bg-white/[0.02] border-white/[0.06] text-white/60 hover:border-white/[0.15]'
                    }`}
                  >
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </button>
                );
              })}
            </div>
          </JewelField>
          <JewelField label="Your message" required>
            <JewelTextarea
              value={feedbackData.message}
              onChange={(e) => setFeedbackData({ ...feedbackData, message: e.target.value })}
              rows={5}
              placeholder="What would you like our People team to know?"
            />
          </JewelField>
          <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
            feedbackData.is_anonymous ? 'border-cyan-400/40 bg-cyan-500/[0.06]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/[0.15]'
          }`}>
            <div
              onClick={() => setFeedbackData({ ...feedbackData, is_anonymous: !feedbackData.is_anonymous })}
              className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors flex-shrink-0 ${
                feedbackData.is_anonymous ? 'bg-cyan-500/30 border-cyan-400/60' : 'bg-white/[0.02] border-white/[0.15]'
              }`}
            >
              {feedbackData.is_anonymous && <Check className="w-3 h-3 text-cyan-300" />}
            </div>
            <div>
              <p className="text-white text-sm">Submit anonymously</p>
              <p className="text-white/40 text-[11px]">We won't attach your name to this feedback</p>
            </div>
          </label>
        </div>
      </JewelModal>
    </div>
  );
};

export default WellbeingPageRevamped;
